import { SortOption } from '../components/Sort/Sort';
import CycleMeasurement from '../enums/cycle-measurement';
import CycleMeasurementSymbol from '../enums/cycle-measurement-symbol';
import SortType from '../enums/sort-type.enum';

export const SIZE_MEASUREMENT = {
  XXS: { height: 12, width: 12 },
  XS: { height: 16, width: 16 },
  SM: { height: 20, width: 20 },
  MD: { height: 24, width: 24 },
  LG: { height: 28, width: 28 },
  XL: { height: 32, width: 32 },
};

export const NETWORK_ERROR_CODE = 'ERR_NETWORK';

export const UNKNOWN_ERROR = 'Oops, something went wrong';

export const PASSWORD_LENGTH_LIMITS = {
  MIN: 8,
  MAX: 99,
};

export const TEXT_LENGTH_LIMITS = {
  MIN: 3,
  MAX: 99,
};

export const EMAIL_LENGTH_LIMITS = {
  MIN: 6,
  MAX: 99,
};

export const SEARCH_TEXT_LENGTH_LIMITS = {
  MIN: 1,
  MAX: 99,
};

export const LENGTH_MFA_CODE = 6;

export const ENTITY_ACTIVATED_STATE = {
  ACTIVATED: 'activated',
  DEACTIVATED: 'deactivated',
};

export const DEVICE_CONNECTION_STATE = {
  CONNECTED: 'Connected',
  DISCONNECTED: 'Disconnected',
};

export const PAGE_TITLE = {
  ADD_CYCLE: 'Add Cycle',
  AUTH_CONFIRM_PASSWORD_RESET: 'Reset password',
  AUTH_FORGOT_PASSWORD: 'Forgot password',
  AUTH_SIGN_IN: 'Sign in',
  CALIBRATION_CERTIFICATES: 'Calibration certificates',
  CHANNEL_QUICK_SET: 'Channel Quick Set',
  CALIBRATION_CERTIFICATE_CREATE: 'Create calibration certificate',
  CALIBRATION_CERTIFICATE_EDIT: 'Edit calibration certificate',
  CONTRACTS: 'Contracts',
  CONTROLLERS: 'Controllers',
  CONTROLLER_CHANNELS: 'Channels',
  CONTROLLER_CHANNEL_FAULTS: 'Channel Faults',
  CONTROLLER_CONSOLE_SETUP: 'Console Setup',
  CONTROLLER_CHANNELS_NAMES: 'Channels Names',
  CONTROLLER_CHANNELS_COLORS: 'Channels Colors',
  CONTROLLER_SETUPS: 'Controller Setup',
  CREATE_CONTROLLER_SETUP: 'Create Controller Setup',
  EDIT_CONTROLLER_SETUP: 'Edit Controller Setup',
  CREATE_CONTRACT: 'Create Contract',
  CREATE_LOCATION: 'Create Client',
  CREATE_MEMBERS: 'Create Member',
  CREATE_RECIPE: 'Create recipe',
  CREATE_RECIPE_STEPS: 'Recipe step create',
  CREATE_SCREEN: 'Create Screen',
  EDIT_SCREEN: 'Edit Screen',
  CYCLE: 'Cycle',
  CYCLES: 'Cycles',
  CYCLE_ATTACHMENTS: 'Attachments',
  CYCLE_REPORT: 'Cycle report',
  EDIT_CONTRACT: 'Edit Contract',
  EDIT_CONTROLLER: 'Edit Controller',
  EDIT_CONTROLLER_CHANNEL: 'Edit channel',
  EDIT_CONTROLLER_FAULT_CONTROL: 'Edit fault control',
  EDIT_CONTROLLER_SYSTEM_FAULTS: 'System Faults',
  EDIT_CONTROLLER_POWER_SHARE: 'Power Share',
  EDIT_CONTROLLER_SETTINGS: 'Edit Controller Settings',
  EDIT_CYCLE: 'Edit Cycle',
  EDIT_LOCATION: 'Edit Client',
  EDIT_MEMBER: 'Edit Member',
  EDIT_RECIPE: 'Edit recipe',
  EDIT_PRE_HEAT_RECIPE: 'Edit pre-heat recipe',
  EDIT_RECIPE_STEPS: 'Recipe step edit',
  EDIT_CHANNELS_NAMES: 'Edit Channels Names',
  EDIT_CHANNELS_COLORS: 'Edit Channels Colors',
  FORBIDDEN_RESOURCE: 'Forbidden resource',
  HOME: 'Dashboard',
  LOCATIONS: 'Clients',
  LOCATION_CONTRACTS: 'Client`s contracts',
  LOCATION_CONTRACT_SCREENS: 'Client contract screens',
  MEMBERS: 'Members',
  NOT_FOUND: 'Not found',
  ORGANIZATIONS: 'Organization',
  PROFILE: 'Profile',
  RECIPES: 'Recipes',
  RECIPE_STEPS: 'Recipe steps',
  SCREEN_CYCLES: 'Cycles by selected screen',
  OVERVIEW: 'Overview',
};

export const PAGE_TITLE_COMPONENT = {
  [PAGE_TITLE.CYCLE]: { NOTE: 'Note', CHART_PREFERENCES: 'Chart preferences' },
};

const intervalUpdateData = Number(import.meta.env.VITE_APP_INTERVAL_UPDATE_DATA);

export const INTERVAL_UPDATE_DATA = intervalUpdateData > 0 ? intervalUpdateData : 10000;
export const EXTENDED_INTERVAL_UPDATE_DATA = INTERVAL_UPDATE_DATA * 3;
export const MAX_DATA_RETENTION_HOURS = 13; 
export const THERMOCOUPLE_LIMITS = {
  MIN: 1,
  MAX: 24,
};

export const DATA_RETRIEVAL_INTERVAL = {
  TEN_MINUETS: 10 * 60 * 1000,
  HALF_AN_HOUR: 30 * 60 * 1000,
  ONE_HOUR: 1 * 60 * 60 * 1000,
  TWO_HOURS: 2 * 60 * 60 * 1000,
  THREE_HOURS: 3 * 60 * 60 * 1000,
};

export const ACTION_BY_PAGE = {
  [PAGE_TITLE.EDIT_MEMBER]: {
    CHANGE_LOCATIONS: 'UPDATE_LOCATIONS',
    CHANGE_ROLE: 'CHANGE_ROLE',
    CHANGE_PASSWORD: 'CHANGE_PASSWORD',
    CHANGE_ACTIVE_STATUS: 'CHANGE_ACTIVE_STATUS',
  },
  [PAGE_TITLE.EDIT_CONTRACT]: {
    SAVE_AS_CLIENT_DEFAULT: 'SAVE_AS_CLIENT_DEFAULT',
  },
  [PAGE_TITLE.CONTROLLERS]: {
    // Must stay distinct from EDIT_MEMBER's CHANGE_ACTIVE_STATUS value - CASL matches
    // subjects by this literal string, so reusing it would let a rule for one
    // leak into the other.
    CHANGE_ACTIVE_STATUS: 'CONTROLLER_CHANGE_ACTIVE_STATUS',
  },
};

export const FRACTION__DIGITS = 2;

export const CYCLE_MEASUREMENT_NAME = {
  [CycleMeasurementSymbol.Celsius]: CycleMeasurement.Celsius,
  [CycleMeasurementSymbol.Fahrenheit]: CycleMeasurement.Fahrenheit,
};
export const CYCLE_MEASUREMENT_SYMBOL = {
  [CycleMeasurement.Celsius]: CycleMeasurementSymbol.Celsius,
  [CycleMeasurement.Fahrenheit]: CycleMeasurementSymbol.Fahrenheit,
};

export const DEFAULT_CYCLE_MEASUREMENT = CYCLE_MEASUREMENT_NAME[CycleMeasurementSymbol.Celsius];

export const SORT_OPTIONS: SortOption[] = [
  {
    name: 'First newest',
    value: SortType.Newest,
  },
  {
    name: 'First oldest',
    value: SortType.Oldest,
  },
  {
    name: 'A-Z',
    value: SortType.AtoZ,
  },
  {
    name: 'Z-A',
    value: SortType.ZtoA,
  },
];

export const LATIN_ALPHANUMERIC_WITH_SPACES_REGEX = /^[a-zA-Z0-9 ]*$/;
